import React, { useState } from 'react';
import { Modal, Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { getSpotWaitingInfoAll } from '../../../api/golf';
import { useSelector } from 'react-redux';
import WaitingIcon from '../../../_icons/WaitingIcon';
import { useQuery } from '@tanstack/react-query';

const GolfSpotWaitingAllListModal = ({ open, handleClose, spots = [], areaId }) => {
  const { t } = useTranslation();
  const [selectedSpot, setSelectedSpot] = useState(1);
  const user = useSelector(state => state.user.user);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '1200px',
    maxWidth: '95vw',
    maxHeight: '95vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  // startTime까지 남은 시간(분) 계산
  const calculateRemainingMinutesToStart = startTime => {
    if (!startTime) return 0;
    try {
      const startTimeDate = new Date(startTime);
      const now = new Date();
      const diffMs = startTimeDate - now;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return diffMinutes > 0 ? diffMinutes : 0;
    } catch (error) {
      return 0;
    }
  };

  // 회원 이름에서 숫자 제거
  const formatMemberName = memberName => {
    const defaultName = '게스트';
    if (!memberName) return defaultName;
    const nameWithoutNumbers = memberName.replace(/\d+/g, '').trim();
    return nameWithoutNumbers || defaultName;
  };

  // 대기자 정보를 포맷팅
  const formatWaitingMember = waiting => {
    // startTime에서 여유시간을 빼서 실제 이용 시작 시간 계산
    const bufferMinute = waiting.bufferMinute || 0;
    let actualUsageStartTime = waiting.startTime;

    if (actualUsageStartTime && bufferMinute > 0) {
      try {
        const startTimeDate = new Date(actualUsageStartTime);
        const actualStartDate = new Date(startTimeDate.getTime() * 60000);
        actualUsageStartTime = actualStartDate.toISOString();
      } catch (error) {
        // 파싱 실패 시 원본 startTime 사용
      }
    }

    return {
      name: formatMemberName(waiting.memberName),
      waitingMinutes: calculateRemainingMinutesToStart(actualUsageStartTime), // 여유시간 제외한 실제 이용 시작 시간
      reservationId: waiting.reservationId,
      memberId: waiting.memberId,
    };
  };

  // API 응답 데이터를 UI에 필요한 형태로 변환 (모든 스팟)
  const transformAllSpotsData = (apiData, fallbackSpots) => {
    // API 응답에서 spots 배열 추출
    let apiSpots = [];
    if (apiData) {
      if (Array.isArray(apiData)) {
        apiSpots = apiData;
      } else if (Array.isArray(apiData?.spots)) {
        apiSpots = apiData.spots;
      }
    }

    // API 데이터가 있으면 사용, 없으면 fallbackSpots 사용
    const rawSpots = apiSpots.length > 0 ? apiSpots : fallbackSpots;

    // 스팟이 없는 경우
    if (!Array.isArray(rawSpots) || rawSpots.length === 0) {
      return [];
    }

    // 각 스팟을 포맷팅
    return rawSpots.map((spot, index) => {
      // 대기자 목록 포맷팅 (API 데이터에서만 가져옴, fallbackSpots에는 대기자 정보가 없을 수 있음)
      const waitingList =
        apiSpots.length > 0 && Array.isArray(spot.waitingList) ? spot.waitingList.map(formatWaitingMember) : [];

      // 스팟 ID 추출
      const spotId = spot.spotId || spot.id || index + 1;

      // 스팟 번호 추출
      const spotNumber = spot.spotNumber || parseInt(spot.spotName?.replace('S', '') || String(index + 1));

      return {
        id: spotId,
        spotNumber,
        waitingList,
      };
    });
  };

  const handleSpotClick = spotId => {
    setSelectedSpot(spotId);
  };

  // 모든 스팟의 대기자 목록 조회
  const { data: formattedSpots = [] } = useQuery({
    queryKey: ['spotWaitingAllList', user?.token, areaId],
    queryFn: () => getSpotWaitingInfoAll(user.token, areaId),
    onSuccess: () => {
      console.log('spotWaitingAllList 조회 성공');
    },
    onError: error => {
      console.log('spotWaitingAllList 조회 실패:', error);
    },
    enabled: !!open && !!user?.token && !!areaId,
    select: data => transformAllSpotsData(data, spots),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: false,
  });

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        {/* 헤더 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '56px',
            px: '32px',
            backgroundColor: '#262626',
            borderRadius: '8px 8px 0 0',
          }}>
          <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#fff' }}>
            {t('Waiting list status')}
          </Typography>
          <Button
            onClick={handleClose}
            sx={{
              minWidth: '32px',
              width: '32px',
              height: '32px',
              p: 0,
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}>
            <CloseIcon />
          </Button>
        </Box>

        {/* 본문 */}
        <Box sx={{ p: '32px', overflowY: 'auto' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '24px',
            }}>
            {formattedSpots.map(spot => (
              <Button
                key={spot.id}
                onClick={() => handleSpotClick(spot.id)}
                sx={{
                  display: 'flex',
                  gap: '16px',
                  height: '64px',
                  p: '8px',
                  border: selectedSpot === spot.id ? '2px solid #0F7F85' : '1px solid #D9D9D9',
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}>
                {/* 타석 번호 */}
                <Box
                  sx={{
                    width: '106px',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: selectedSpot === spot.id ? '#0F7F85' : '#F2F2F2',
                    borderRadius: '8px',
                  }}>
                  <Typography
                    sx={{
                      fontSize: '32px',
                      fontWeight: '700',
                      color: selectedSpot === spot.id ? '#fff' : '#A6A6A6',
                    }}>
                    {spot.spotNumber}
                  </Typography>
                </Box>

                {/* 대기자 정보 */}
                <Box
                  sx={{
                    flex: 1,
                    height: '100%',
                    display: 'flex',
                    gap: '48px',
                    alignItems: 'center',
                    px: '16px',
                  }}>
                  {spot.waitingList.length > 0 ? (
                    spot.waitingList.map((member, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          gap: '16px',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '93px',
                        }}>
                        <Box sx={{ width: '8px', height: '32px', display: 'flex', alignItems: 'center' }}>
                          <svg width="8" height="24" viewBox="0 0 8 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M4 0C4.60621 0 5.18759 0.252856 5.61624 0.702944C6.0449 1.15303 6.28571 1.76348 6.28571 2.4C6.28571 3.03652 6.0449 3.64697 5.61624 4.09706C5.18759 4.54714 4.60621 4.8 4 4.8C3.39379 4.8 2.81241 4.54714 2.38376 4.09706C1.9551 3.64697 1.71429 3.03652 1.71429 2.4C1.71429 1.76348 1.9551 1.15303 2.38376 0.702944C2.81241 0.252856 3.39379 0 4 0ZM2.28571 6H5.71429C6.3205 6 6.90188 6.25286 7.33053 6.70294C7.75918 7.15303 8 7.76348 8 8.4V15H6.28571V24H1.71429V15H0V8.4C0 7.76348 0.240816 7.15303 0.669471 6.70294C1.09813 6.25286 1.67951 6 2.28571 6Z"
                              fill="#0F7F85"
                            />
                          </svg>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '52px' }}>
                          <Typography sx={{ fontSize: '18px', fontWeight: '500', color: '#000' }}>
                            {member.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0px' }}>
                            <Typography sx={{ fontSize: '16px', fontWeight: '400', color: '#000' }}>
                              {member.waitingMinutes}
                            </Typography>
                            <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#737373' }}>
                              {t('minutes')}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ display: 'flex', gap: '9px', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <WaitingIcon width={24} height={24} fill="#0F7F85" />
                      </Box>
                      <Typography sx={{ fontSize: '20px', fontWeight: '500', color: '#A6A6A6' }}>
                        {t('No waiting members')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Button>
            ))}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default GolfSpotWaitingAllListModal;
